import { useEffect, useState } from 'react'
import { Cloud, CloudOff, RefreshCw, AlertTriangle } from 'lucide-react'
import { getBackupStatus, type BackupStatus } from '@/api/sync'

const POLL_MS = 30_000

type LocalStatus = Extract<BackupStatus, { mode: 'local' }>

function timeAgo(iso: string | null): string {
  if (!iso) return 'todavía no se ha respaldado nada en esta sesión'
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutes < 1) return 'último respaldo hace menos de un minuto'
  if (minutes < 60) return `último respaldo hace ${minutes} min`
  return `último respaldo hace ${Math.floor(minutes / 60)} h`
}

function describe(s: LocalStatus): { label: string; title: string; tone: string; Icon: typeof Cloud } {
  const since = timeAgo(s.last_synced_at)
  if (s.needs_attention > 0) {
    return {
      label: `${s.needs_attention} con error`,
      title: `${s.needs_attention} cambio(s) no se pudieron respaldar en la nube y necesitan revisión. Todo sigue guardado en este equipo.`,
      tone: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
      Icon: AlertTriangle,
    }
  }
  if (s.pending > 0) {
    const offline = s.degraded || !s.replicating
    return {
      label: offline ? `Sin conexión · ${s.pending} por respaldar` : `Respaldando ${s.pending}…`,
      title: offline
        ? `No hay conexión con la nube. Todo sigue funcionando y se guarda en este equipo; ${s.pending} cambio(s) se respaldarán solos al volver la conexión (${since}).`
        : `Respaldando ${s.pending} cambio(s) en la nube (${since}).`,
      tone: offline ? 'text-white/55 bg-white/5 border-white/15' : 'text-sky-300 bg-sky-500/10 border-sky-500/25',
      Icon: offline ? CloudOff : RefreshCw,
    }
  }
  return {
    label: 'Respaldo al día',
    title: `Todos los cambios están respaldados en la nube (${since}).`,
    tone: 'text-emerald-300/80 bg-emerald-500/5 border-emerald-500/15',
    Icon: Cloud,
  }
}

// Shows whether this local-first install's changes have reached Supabase.
// The point is reassurance, not alarm: with no internet the app keeps working
// on its local database, and this badge is the only place that says so.
// Renders nothing on installs where Supabase is the live database, or if the
// status can't be read.
export function BackupStatusBadge() {
  const [status, setStatus] = useState<LocalStatus | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const s = await getBackupStatus()
        if (!cancelled) setStatus(s.mode === 'local' ? s : null)
      } catch {
        if (!cancelled) setStatus(null)
      }
    }
    load()
    const id = setInterval(load, POLL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  if (!status) return null
  const { label, title, tone, Icon } = describe(status)
  const spinning = Icon === RefreshCw

  return (
    <span
      role="status"
      title={title}
      className={`flex items-center gap-1.5 text-[11px] font-medium border px-2.5 py-1 rounded-full select-none ${tone}`}
    >
      <Icon className={`w-3 h-3 shrink-0 ${spinning ? 'animate-spin' : ''}`} aria-hidden="true" />
      {label}
    </span>
  )
}
